import React, {createContext, useCallback, useContext, useReducer, useMemo} from "react";
import {Verification} from "../models";
import {LoadingState} from "../helper/utils";
import {getIntents, revokeIntent} from "../services/requests";


interface State {
    message?: {desc: string, title: string}
    intents: Verification[]
    loadingState: LoadingState
}
type Action =
| {type: "set_intents", payload: Verification[]}
| {type: "add_intent"; payload: Verification}
| {type: "remove_intent"; payload: string}
| {type: "set_loading_state"; payload: LoadingState}
| {type: "set_error", payload?: {desc: string, title: string}}

// The backend's rows say verificationID; the app's VerificationID.
function toVerification(v: Verification & {verificationID?: string}): Verification {
    return {intent: v.intent, VerificationID: v.VerificationID ?? v.verificationID ?? ""};
}

const VerificationContext = createContext<ReturnType<typeof useVerificationValue> | undefined>(undefined);
const initialState: State = {intents: [], loadingState: LoadingState.None};

function createRequestActions(dispatch: React.Dispatch<Action>) {
    return {
        addIntent: (payload: Verification) => dispatch(
            {type: "add_intent", payload}
        ),
        clearError: () => dispatch({type: "set_error"}),
        removeIntent: (payload: string) => dispatch(
            {type: "remove_intent", payload}
        ),
        setIntents: (payload: Verification[]) => dispatch(
            {type: "set_intents", payload}
        ),
        setRequeststate: (payload: LoadingState) => dispatch(
            {type: "set_loading_state", payload}
        ),
    };
}

function reducer(state: State, action: Action): State {
    switch (action.type) {
        case "set_loading_state": return {...state, loadingState: action.payload};
        case "set_error": return {...state, message: action.payload};
        case "set_intents": return {...state, intents: action.payload.map(toVerification)};
        case "add_intent": {
            const intents = state.intents.concat([action.payload]);
            return {...state, intents};
        }
        case "remove_intent": {
            const intents = state.intents.filter(
                (v) => v.VerificationID !== action.payload
            );
            return {...state, intents};
        }
        default: return state;
    }
}

function useVerificationValue() {
    const [state, dispatch] = useReducer(reducer, initialState);
    const actions = useMemo(() => createRequestActions(dispatch), [dispatch]);
    const canCreate = useMemo(function () {
        return state.intents.filter((v) => v.intent === "CREATION").length <= 0;
    }, [state.intents]);
    const canAct = useMemo(function () {
        return state.intents.filter((v) => v.intent !== "CREATION").length <= 0;
    }, [state.intents]);
    const has = useCallback(function (id: string) {
        return state.intents.filter((i) => i.VerificationID === id).length > 0;
    }, [state.intents]);
    const get = useCallback(function (id: string): Verification | undefined {
        const res = state.intents.filter((i) => i.VerificationID === id);
        if (res[0]) {
            return res[0];
        }
    }, [state.intents]);
    const fetchIntents = useCallback(async function () {
        let res;
        actions.setRequeststate(LoadingState.Pending);
        res = await getIntents();
        if (res.type === "success") {
            dispatch({type: "set_intents", payload: res.value});
            actions.setRequeststate(LoadingState.Success);
        } else {
            actions.setRequeststate(LoadingState.Error);
            dispatch({type: "set_error", payload: {
                desc: res.message,
                title: "Intent Fetching Error"
            }});
        }
    }, [actions]);
    /** Withdraw the pending intents of a kind; false when one couldn't be. */
    const revoke = useCallback(async function (kind: "creation" | "action"): Promise<boolean> {
        const pending = state.intents.filter(
            (v) => (v.intent === "CREATION") === (kind === "creation")
        );
        let ok = true;
        for (const v of pending) {
            const res = await revokeIntent(v.VerificationID);
            if (res.type === "success") {
                actions.removeIntent(v.VerificationID);
            } else {
                ok = false;
                dispatch({type: "set_error", payload: {
                    desc: res.message,
                    title: "Cancellation Error"
                }});
            }
        }
        return ok;
    }, [actions, state.intents]);
    return useMemo(
        () => ({actions, canAct, canCreate, dispatch, fetchIntents, get, has, revoke, state}),
        [actions, canAct, canCreate, fetchIntents, get, has, revoke, state]
    );
}

export function VerificationProvider(props: {children: React.ReactNode}) {
    const value = useVerificationValue();
    return <VerificationContext.Provider value={value}>
        {props.children}
    </VerificationContext.Provider>;
}

export function useVerification() {
    const context = useContext(VerificationContext);
    if (!context) {
        throw new Error("useVerification must be used within a VerificationProvider");
    }
    return context;
}
